import React, { useState } from "react";
import OrderHistoryTable from "../../components/OrderHistoryTable";
import useOrders from "../../hooks/useOrders";
import useOrderById from "../../hooks/useOrderById";
import useIndicativePriceSummary from "../../hooks/useIndicativePriceSummary";
import axios from "axios";
import MessageBox from "../../components/MessageBox";

import "./OrderHistoryContainer.scss";
import usePermissions from "../../hooks/usePermissions";
import PageContainer from "../PageContainer";
import useErrorHandler from "../../hooks/userErrorHandler";
import responseToSubmissionResultItems from "../../utils/responseToSubmissionResultItems";
import useCurrencies from "../../hooks/useCurrencies";
import useBranches from "../../hooks/useBranches";
import usePersistentState from "../../hooks/usePersistenState/usePersistentState";

const OrderHistoryContainer = () => {
  const [page, setPage] = usePersistentState("default-order-history-page", 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [selectedOrderId, setSelectedOrderId] = useState<any>(null);
  const [selectedOrderType, setSelectedOrderType] = useState<any>(null);
  const [authorisedOrderResult, setAuthorisedOrderResult] = useState<any>(null);
  const [processedCount, setProcessedCount] = useState<number>(0);
  const { currencies, currenciesLoading } = useCurrencies();
  const [filter, setFilter] = usePersistentState(
    "default-order-history-filter",
    {
      type: "",
      branchName: "",
      status: "",
      currency: -1,
      dateRequiredFrom: "",
      dateRequiredTo: "",
      datePlacedFrom: "",
      datePlacedTo: ""
    }
  );
  const [openOrderResultsModal, setOpenOrderResultsModal] = useState<boolean>(
    false
  );
  const [openOrderProcessedSnackBar, setOpenOrderProcessedSnackBar] = useState<
    boolean
  >(false);
  const [
    openCurrencyUnavailableSnackBar,
    setOpenCurrencyUnavailableSnackBar
  ] = useState<boolean>(false);
  const [
    openConcurrentAuthorisationSnackBar,
    setOpenConcurrentAuthorisationSnackBar
  ] = useState<boolean>(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState<boolean>(false);
  const [sortCriteria, setSortCriteria] = usePersistentState(
    "default-order-history-sort-criteria",
    {
      sortBy: null,
      sortDirection: null
    }
  );
  const { branches, branchesLoading } = useBranches();
  const setError = useErrorHandler();

  const {
    orders,
    ordersLoading,
    currentPage,
    pageSize,
    pageCount,
    totalCount
  } = useOrders(
    page,
    rowsPerPage,
    filter,
    sortCriteria.sortBy,
    sortCriteria.sortDirection,
    [
      processedCount,
      page,
      rowsPerPage,
      sortCriteria.sortBy,
      sortCriteria.sortDirection,
      filter
    ]
  );
  const { order, orderLoading } = useOrderById(
    selectedOrderId,
    selectedOrderType,
    [processedCount, selectedOrderId, selectedOrderType]
  );
  const { priceSummary, priceSummaryLoading } = useIndicativePriceSummary(
    order,
    selectedOrderType && selectedOrderType === "Return"
  );
  const permissions = usePermissions();

  function doProcessWithReferees(ordersWithReferees: any) {
    (async () => {
      try {
        setOpenOrderResultsModal(false);
        if (ordersWithReferees !== [] || null) {
          ordersWithReferees.forEach(async (order: any) => {
            await axios.put(
              `/api/Orders/${order.id}/referee`,
              { version: order.version, referee: order.userDetails.id },
              {
                headers: {
                  "Content-Type": "application/json"
                }
              }
            );
            setProcessedCount(processedCount => processedCount + 1);
          });
        }
      } catch (err) {
        if (
          err &&
          err.response &&
          err.response.status === 409 &&
          err.response.data.problems &&
          err.response.data.problems[0].problemKey ===
            "Order.Currency.Unavailable"
        ) {
          setOpenCurrencyUnavailableSnackBar(true);
        } else if (err.message === "Network Error") {
          document.location.href = "/";
        } else {
          setError("Unable to process this order with referees", err);
        }
      }
    })();
  }

  async function doProcessFor(order: any) {
    try {
      if (order && order !== null) {
        setIsProcessingOrder(true);
        await axios.put(
          order.type === "Order"
            ? `/api/Orders/${order.id}/process`
            : `/api/Orders/currency-returns/${order.id}/process`,
          {
            version: order.version
          },
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        );
        setProcessedCount(processedCount => processedCount + 1);
        setIsProcessingOrder(false);
      }
    } catch (err) {
      if (
        err &&
        err.response &&
        err.response.status === 409 &&
        err.response.data.problems &&
        err.response.data.problems[0].problemKey ===
          "Order.Currency.Unavailable"
      ) {
        setOpenCurrencyUnavailableSnackBar(true);
        setIsProcessingOrder(false);
      } else if (
        err &&
        err.response &&
        err.response.status === 409 &&
        err.response.data.problems &&
        err.response.data.problems[0].problemKey ===
          "CurrencyReturn.UpdatedByAnotherUser"
      ) {
        setError(
          "Unable to process this return because it has been updated by someone else",
          err
        );
      } else if (
        err &&
        err.response &&
        err.response.status === 409 &&
        err.response.data.problems &&
        err.response.data.problems[0].problemKey ===
          "Order.UpdatedByAnotherUser"
      ) {
        setError(
          "Unable to process this order because it has been updated by someone else",
          err
        );
      } else if (err.message === "Network Error") {
        document.location.href = "/";
      } else {
        if (selectedOrderType === "Order") {
          setError("Unable to process this order", err);
        } else {
          setError("Unable to process this return", err);
        }
      }
      throw err;
    }
  }

  function doAuthorise(authoriseDetails: any) {
    (async () => {
      setOpenOrderResultsModal(false);
      try {
        if (selectedOrderId !== null) {
          const response = await axios.put(
            `/api/Orders/${selectedOrderId}/authorise`,
            authoriseDetails,
            {
              headers: {
                "Content-Type": "application/json"
              }
            }
          );
          setAuthorisedOrderResult({
            ordered: responseToSubmissionResultItems(response.data.ordered),
            requiresAuthorisation: responseToSubmissionResultItems(
              response.data.requiresAuthorisation
            ),
            failed: responseToSubmissionResultItems(response.data.failed)
          });
          setProcessedCount(processedCount => processedCount + 1);
          setOpenOrderResultsModal(true);
        }
      } catch (err) {
        if (
          err &&
          err.response &&
          err.response.status === 409 &&
          err.response.data.problems &&
          err.response.data.problems[0].problemKey ===
            "Order.UpdatedByAnotherUser"
        ) {
          setProcessedCount(processedCount => processedCount + 1);
          setOpenOrderResultsModal(false);
          setOpenConcurrentAuthorisationSnackBar(true);
        } else if (
          err &&
          err.response &&
          err.response.status === 409 &&
          err.response.data.problems &&
          err.response.data.problems[0].problemKey ===
            "Order.Currency.Unavailable"
        ) {
          setOpenCurrencyUnavailableSnackBar(true);
        } else if (err.message === "Network Error") {
          document.location.href = "/";
        } else {
          setError("Unable to authorise this order", err);
        }
      }
    })();
  }

  function doCancellation(orderType: string, orderDetails: any) {
    (async () => {
      try {
        if (selectedOrderId !== null) {
          const response = await axios.put(
            orderType === "Order"
              ? `/api/Orders/${selectedOrderId}/cancel`
              : `/api/Orders/currency-returns/${selectedOrderId}/cancel`,
            orderDetails,
            {
              headers: {
                "Content-Type": "application/json"
              }
            }
          );
          setProcessedCount(processedCount => processedCount + 1);
        }
      } catch (err) {
        setError(
          `Unable to cancel ${
            orderType === "Order" ? "order" : "return"
          } ${selectedOrderId}`,
          err
        );
      }
    })();
  }

  return (
    <PageContainer title="Order History">
      <div className="OrderHistoryContainer-container">
        <OrderHistoryTable
          isLoadingOrders={ordersLoading}
          isLoadingOrder={
            orderLoading || isProcessingOrder || priceSummaryLoading
          }
          orderHistory={orders}
          selectedOrder={order}
          selectedOrderType={selectedOrderType}
          priceSummary={priceSummary}
          onOrderSelect={(id: any, orderType: any) => {
            setSelectedOrderId(null);
            setSelectedOrderType(orderType);
            setSelectedOrderId(id);
          }}
          onProcess={async () => {
            await doProcessFor({ ...order, type: selectedOrderType });
            setOpenOrderProcessedSnackBar(true);
          }}
          onProcessAll={async (orders: any[]) => {
            for (let i = 0; i < orders.length; i++) {
              const path =
                (orders[i].type === "Order"
                  ? `/api/Orders/${orders[i].id}?ts=`
                  : `/api/Orders/currency-returns/${orders[i].id}?ts=`) +
                new Date().getTime();
              const response = await axios.get(path);
              await doProcessFor({ ...response.data, type: orders[i].type });
            }
          }}
          onAuthorise={doAuthorise}
          onCancelOrder={doCancellation}
          openOrderResultsModal={openOrderResultsModal}
          authorisedOrderResult={authorisedOrderResult}
          onReferredOrders={(referees: any[]) => {
            doProcessWithReferees(referees);
          }}
          permissions={permissions}
          paginationInfo={{
            currentPage: currentPage || (page ? page - 1 : 0),
            pageSize: pageSize,
            pageCount: pageCount,
            totalCount: totalCount
          }}
          onChangePageNumber={(page: number) => setPage(page)}
          onChangePageSize={(rowsPerPage: any) => setRowsPerPage(rowsPerPage)}
          onSort={(x: any) => setSortCriteria(x)}
          processedCount={processedCount}
          onFilter={(x: any) => setFilter(x)}
          currencies={currencies}
          branches={branches}
          sortCriteria={sortCriteria}
          filter={filter}
        />
        {openOrderProcessedSnackBar ? (
          <MessageBox
            severity="success"
            onClose={() => setOpenOrderProcessedSnackBar(false)}
            message={`Order with id ${selectedOrderId} processed successfully`}
          />
        ) : (
          <></>
        )}
        {openConcurrentAuthorisationSnackBar ? (
          <MessageBox
            severity="info"
            onClose={() => setOpenConcurrentAuthorisationSnackBar(false)}
            message={"Order authorised by another user. Refreshing table."}
          />
        ) : (
          <></>
        )}
        {openCurrencyUnavailableSnackBar ? (
          <MessageBox
            severity="error"
            onClose={() => setOpenCurrencyUnavailableSnackBar(false)}
            message={
              selectedOrderType === "Order"
                ? "The currency/denominations selected are currently unavailable."
                : "The currency selected is currently unavailable."
            }
          />
        ) : (
          <></>
        )}
      </div>
    </PageContainer>
  );
};

export default OrderHistoryContainer;
